import React, { useState, useContext, useEffect } from "react";
import dayjs from "dayjs";

import { makeStyles } from "@mui/styles";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";

// Bootstrap
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";

// My Files
import { DatabaseContext, UserContext } from "../../../context";
import CommentInput from "./CommentInput";
import FileComment from "./FileComment";
import {
    repliesTo,
    topLevelComments as visibleTopLevelComments,
    visibleComments as onlyVisible,
} from "./commentThreads";

const useStyles = makeStyles((theme) => ({
    root: { paddingBottom: 15 },
    commentCountText: {
        paddingTop: 15,
        paddingLeft: 15,
        paddingBottom: 5,
        [theme.breakpoints.down("md")]: { paddingLeft: 15 },
    },
}));

export default function CommentSection(props) {
    const classes = useStyles();

    const { comments, users } = useContext(DatabaseContext);

    const [fileComments, setFileComments] = useState([]);

    const [userCanComment, setUserCanComment] = useState(false);

    const { currentUserData } = useContext(UserContext);

    useEffect(() => {
        let array = [];

        for (let i = 0; i < comments.length; i++) {
            let comment = comments[i];

            // Deleted comments are kept here rather than filtered out, because
            // whether one is shown depends on its replies. See below.
            if (comment.file === props.file.key) {
                if (comment.userObject === undefined) {
                    let user = users.find((u) => u.key === comment.user);

                    if (user !== undefined) {
                        comment.userObject = user;
                    }
                }

                comment.dateString = "";

                array.push(comment);
            }
        }

        array.sort((a, b) =>
            dayjs(a.date) < dayjs(b.date)
                ? 1
                : dayjs(b.date) < dayjs(a.date)
                ? -1
                : 0
        );

        setFileComments(array);

        if (currentUserData && currentUserData.permissions) {
            if (currentUserData.permissions.comment) {
                setUserCanComment(true);
            } else {
                setUserCanComment(false);
            }
        }
    }, [comments, users, props, currentUserData]);

    const visibleComments = onlyVisible(fileComments);
    const topLevelComments = visibleTopLevelComments(fileComments);

    if (props.modern) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    minHeight: { md: 420 },
                    maxHeight: { md: "calc(100vh - 112px)" },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: { xs: 2, sm: 2.5 },
                        py: 2,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <ChatBubbleOutlineIcon
                        sx={{ color: "primary.main", fontSize: 22 }}
                    />
                    <Typography
                        variant="h6"
                        component="h2"
                        sx={{ lineHeight: 1 }}
                    >
                        Comments
                    </Typography>
                    <Chip
                        label={visibleComments.length}
                        size="small"
                        aria-label={`${visibleComments.length} comments`}
                        sx={{
                            ml: "auto",
                            minWidth: 32,
                            backgroundColor: "rgba(0, 149, 61, 0.09)",
                            color: "primary.main",
                            fontWeight: 700,
                        }}
                    />
                </Box>

                {userCanComment ? (
                    <Box
                        sx={{
                            p: { xs: 2, sm: 2.5 },
                            borderBottom: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <CommentInput
                            file={props.file}
                            isReply={false}
                            avatarSize="medium"
                            videoPlayer={props.videoPlayer}
                            setIsTyping={props.setIsTyping}
                            modern
                        />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            px: 2.5,
                            py: 1.5,
                            backgroundColor: "rgba(0, 149, 61, 0.045)",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            You can read comments, but your account cannot add
                            them.
                        </Typography>
                    </Box>
                )}

                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: { md: "auto" },
                        p: { xs: 2, sm: 2.5 },
                    }}
                >
                    {topLevelComments.length === 0 ? (
                        <Box
                            sx={{
                                minHeight: 150,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                textAlign: "center",
                                color: "text.secondary",
                                px: 2,
                            }}
                        >
                            <ChatBubbleOutlineIcon
                                sx={{ fontSize: 34, mb: 1, opacity: 0.45 }}
                            />
                            <Typography
                                variant="subtitle2"
                                sx={{ color: "text.primary", fontWeight: 700 }}
                            >
                                No comments yet
                            </Typography>
                            {userCanComment && (
                                <Typography variant="body2">
                                    {props.videoPlayer
                                        ? "Start the conversation or add a note at the current point in the video."
                                        : "Start the conversation or add a note about this file."}
                                </Typography>
                            )}
                        </Box>
                    ) : (
                        topLevelComments.map((comment, index) => (
                            <FileComment
                                key={comment.key}
                                file={props.file}
                                comment={comment}
                                avatarSize="medium"
                                videoPlayer={props.videoPlayer}
                                isReply={false}
                                setIsTyping={props.setIsTyping}
                                replies={repliesTo(fileComments, comment.key)}
                                modern
                                showDivider={index < topLevelComments.length - 1}
                            />
                        ))
                    )}
                </Box>
            </Box>
        );
    }

    return (
        <>
            <Container fluid className={classes.root}>
                <Row>
                    <h6 className={classes.commentCountText}>
                        {visibleComments.length === 1
                            ? visibleComments.length + " Comment"
                            : visibleComments.length + " Comments"}
                    </h6>
                </Row>
                <Row>
                    {userCanComment && (
                        <CommentInput
                            file={props.file}
                            isReply={false}
                            avatarSize={"medium"}
                            videoPlayer={props.videoPlayer}
                            setIsTyping={props.setIsTyping}
                        />
                    )}
                </Row>

                {topLevelComments.map((comment) => (
                        <Row key={comment.key}>
                            <FileComment
                                file={props.file}
                                comment={comment}
                                avatarSize="medium"
                                videoPlayer={props.videoPlayer}
                                isReply={false}
                                setIsTyping={props.setIsTyping}
                                replies={repliesTo(fileComments, comment.key)}
                            />
                        </Row>
                    ))}
            </Container>
        </>
    );
}
